/**
 * Catalog (Brief 185) — one page, three shelves, absorbing every product the
 * customer hasn't opened into a browsable surface so the nav never sprawls.
 *
 * Rendered from LIVE discovery data: the product families + summaries come from
 * GET /v1/catalog/products, the LLM card's task count + cost-plus terms come
 * from GET /v1/llm/tasks (so a registry-created task shows up with ZERO
 * dashboard deploys), and the in-use badges come from GET /v1/account/metrics.
 * Per the operator rider: a failed price fetch NEVER blocks the page — the card
 * just renders without its price chip.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, PackageOpen } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  accountMetricsKey,
  catalogKey,
  catalogFullKey,
  fetchAccountMetrics,
  fetchCatalog,
  fetchCatalogFull,
  fetchLlmTasks,
  llmTasksKey,
} from '@/lib/queries';

type Shelf = 'Store & hand off' | 'Documents & identity' | 'Compute & transform';

interface CatalogItem {
  key: string;
  name: string;
  shelf: Shelf;
  blurb: string;
  /** Service-key prefixes that indicate this product is in use. */
  usePrefixes: string[];
  /** The in-app workspace, when one exists. */
  workspace?: string;
}

// The shelf taxonomy is a presentation decision (the design doc's three
// shelves). Live data enriches each card; nothing here gates on a deploy for a
// re-priced product or a new registry task.
const ITEMS: CatalogItem[] = [
  { key: 'baton', name: 'Baton', shelf: 'Store & hand off', blurb: 'Prepaid shared storage + hash-chained ledger primitives for agent hand-off, with collaborator tokens and document-witness.', usePrefixes: ['baton'], workspace: '/dashboard/batons' },
  { key: 'courier', name: 'Courier', shelf: 'Store & hand off', blurb: 'Two-way text bridge between agents and humans (Telegram, email, SMS) plus agent-to-agent messaging.', usePrefixes: ['bridge', 'courier'], workspace: '/dashboard/courier' },
  { key: 'esign', name: 'E-sign', shelf: 'Documents & identity', blurb: 'Send a document for signature and track the envelope to completion.', usePrefixes: ['esigndoc', 'esign'], workspace: '/dashboard/esign' },
  { key: 'idverify', name: 'ID verification', shelf: 'Documents & identity', blurb: 'Document / liveness / biometric verification with an optional AML screen and a hosted handoff URL.', usePrefixes: ['idverify'], workspace: '/dashboard/idverify' },
  { key: 'doc', name: 'Doc convert', shelf: 'Documents & identity', blurb: 'Markup + structured-document conversion (markdown, docx, odt, epub, latex, …).', usePrefixes: ['doc'], workspace: '/dashboard/cputools' },
  { key: 'cputools', name: 'cputools', shelf: 'Compute & transform', blurb: 'Pure-compute utilities billed per call: PDF, CSV, image, data/ETL, archive, pipelines.', usePrefixes: ['cputools', 'pdf', 'csv', 'image', 'data'], workspace: '/dashboard/cputools' },
  { key: 'llm', name: 'LLM tasks', shelf: 'Compute & transform', blurb: 'Task-based LLM calls (pick a task + tier); pay the actual provider cost plus a small markup.', usePrefixes: ['llm'], workspace: '/dashboard/llm' },
  { key: 'media', name: 'Media', shelf: 'Compute & transform', blurb: 'Audio/video transcode, trim, thumbnail, and probe via ffmpeg.', usePrefixes: ['media'], workspace: '/dashboard/cputools' },
];

const SHELVES: Shelf[] = ['Store & hand off', 'Documents & identity', 'Compute & transform'];

export function Catalog() {
  // All reads degrade gracefully — the page renders even if any fails.
  const catalogQ = useQuery({ queryKey: catalogKey, queryFn: fetchCatalog });
  const catalogFullQ = useQuery({ queryKey: catalogFullKey, queryFn: fetchCatalogFull });
  const llmQ = useQuery({ queryKey: llmTasksKey, queryFn: fetchLlmTasks });
  const metricsQ = useQuery({ queryKey: accountMetricsKey, queryFn: fetchAccountMetrics });

  const usedPrefixes = new Set((metricsQ.data?.usedProducts ?? []).map((p) => p.product));

  // Live family summaries from /v1/catalog/products override the static blurbs
  // where a family matches (keyed by lower-cased family name). Keeps the copy
  // for Baton/Courier/cputools sourced from the live catalog, not hardcoded.
  const liveSummaries: Record<string, string> = {};
  const families = (catalogQ.data?.['product_families'] as Array<Record<string, unknown>> | undefined) ?? [];
  for (const f of families) {
    if (typeof f['name'] === 'string' && typeof f['summary'] === 'string') {
      liveSummaries[(f['name'] as string).toLowerCase()] = f['summary'] as string;
    }
  }

  // Live price chip per product, now sourced from the One True Catalog
  // (GET /v1/catalog): bucket the 129 ops by each family's service-key prefixes,
  // show the op count + a price floor. Still degrades gracefully — a failed
  // fetch just drops the chip (the operator's rider). LLM keeps its cost-plus
  // wording; baton/pipeline are metered.
  const catalogOps = catalogFullQ.data?.ops ?? [];
  function microsToUsd(micros: number): string {
    const d = micros / 1_000_000;
    return `$${d.toLocaleString('en-US', {
      minimumFractionDigits: d < 1 ? 4 : 2,
      maximumFractionDigits: 6,
    })}`;
  }
  // Family → catalog-op matcher (opIds are serviceKeys; media/doc live under
  // cputools.* but have their own cards, so the cputools card excludes them).
  const FAMILY_MATCH: Record<string, (opId: string) => boolean> = {
    baton: (id) => id === 'baton',
    courier: (id) => id.startsWith('bridge.'),
    esign: (id) => id.startsWith('esign.'),
    idverify: (id) => id.startsWith('idverify.'),
    doc: (id) => id.startsWith('cputools.doc.'),
    media: (id) => id.startsWith('cputools.media.'),
    cputools: (id) =>
      id.startsWith('cputools.') &&
      !id.startsWith('cputools.doc.') &&
      !id.startsWith('cputools.media.'),
    llm: (id) => id.startsWith('llm.'),
  };
  function priceChip(item: CatalogItem): string | null {
    if (item.key === 'llm' && llmQ.data) {
      return `${llmQ.data.tasks.length} tasks · cost + ${llmQ.data.pricing.markupPct}%`;
    }
    if (item.key === 'baton') return 'metered · live quote';
    const match = FAMILY_MATCH[item.key];
    const ops = match ? catalogOps.filter((o) => match(o.opId)) : [];
    if (ops.length === 0) return null;
    const floors = ops
      .filter((o) => o.priceKind === 'fixed' && typeof o.priceMicros === 'number' && (o.priceMicros as number) > 0)
      .map((o) => o.priceMicros as number);
    const countLabel = `${ops.length} op${ops.length === 1 ? '' : 's'}`;
    return floors.length ? `${countLabel} · from ${microsToUsd(Math.min(...floors))}` : countLabel;
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Catalog</h1>
        <p className="text-muted-foreground">
          Everything Relaystation does, on one prepaid balance and one API key.
          Open a product to start using it.
        </p>
      </header>

      {SHELVES.map((shelf) => (
        <section key={shelf} className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {shelf}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {ITEMS.filter((i) => i.shelf === shelf).map((item) => {
              const inUse = item.usePrefixes.some((p) => usedPrefixes.has(p));
              const price = priceChip(item);
              return (
                <Card key={item.key} className="flex flex-col">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-base">{item.name}</CardTitle>
                      {inUse ? <Badge variant="success">In use</Badge> : null}
                    </div>
                    {price ? (
                      <CardDescription className="font-mono text-xs">{price}</CardDescription>
                    ) : null}
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col justify-between gap-4">
                    <p className="text-sm text-muted-foreground">
                      {liveSummaries[item.name.toLowerCase()] ?? item.blurb}
                    </p>
                    {item.workspace ? (
                      <Button asChild variant={inUse ? 'default' : 'outline'} size="sm" className="gap-1 self-start">
                        <Link to={item.workspace}>
                          {inUse ? 'Open' : 'Try it'}
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </Button>
                    ) : null}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      ))}

      <Card className="border-dashed">
        <CardContent className="flex items-center gap-3 py-6 text-sm text-muted-foreground">
          <PackageOpen className="h-5 w-5" />
          More products land here automatically — this page reads the live
          catalog, so a new product or a new LLM task shows up without an update.
        </CardContent>
      </Card>
    </div>
  );
}
